import React, { useEffect, useState, useCallback } from "react";
import Navbar from "../components/Navbar";
import { authFetch, useAuth } from "../context/AuthContext";
import { ShieldCheck, Crown, EyeSlash, Eye, Trash, MagnifyingGlass, Star, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fileUrl } from "../components/PhotoGallery";

const SIGN_GLYPH = {
    Aries: "♈", Taurus: "♉", Gemini: "♊", Cancer: "♋", Leo: "♌", Virgo: "♍",
    Libra: "♎", Scorpio: "♏", Sagittarius: "♐", Capricorn: "♑", Aquarius: "♒", Pisces: "♓",
};

export default function Admin() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState({ premium: "", verified: "", admin: "" });
    const [loading, setLoading] = useState(false);
    const [recent, setRecent] = useState([]);
    const [recentHours, setRecentHours] = useState(24);

    const loadStats = useCallback(async () => {
        try {
            const r = await authFetch("get", "/admin/stats");
            setStats(r.data);
        } catch { /* ignore */ }
    }, []);

    const loadRecent = useCallback(async (h) => {
        try {
            const hours = h || recentHours;
            const r = await authFetch("get", `/admin/queue/recent?hours=${hours}`);
            setRecent(r.data.users);
        } catch { /* ignore */ }
    }, [recentHours]);

    const loadUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (search) params.set("search", search);
            if (filter.premium !== "") params.set("filter_premium", filter.premium);
            if (filter.verified !== "") params.set("filter_verified", filter.verified);
            if (filter.admin !== "") params.set("filter_admin", filter.admin);
            params.set("limit", "100");
            const r = await authFetch("get", `/admin/users?${params}`);
            setUsers(r.data.users);
            setTotal(r.data.total);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load users");
        } finally {
            setLoading(false);
        }
    }, [search, filter]);

    useEffect(() => { loadStats(); loadUsers(); loadRecent(); }, [loadStats, loadUsers, loadRecent]);

    const setFlag = async (uid, action, value, label) => {
        try {
            await authFetch("post", `/admin/users/${uid}/${action}`, { value });
            toast.success(`${label} ${value ? "enabled" : "disabled"}`);
            loadUsers();
            loadStats();
            loadRecent();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Action failed");
        }
    };

    const deleteUser = async (uid, name) => {
        if (!window.confirm(`Permanently delete ${name}? This removes all their data.`)) return;
        try {
            await authFetch("delete", `/admin/users/${uid}`);
            toast.success("User deleted");
            loadUsers();
            loadStats();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
    };

    if (!user) return null;

    return (
        <>
            <Navbar />
            <main className="min-h-screen pt-28 pb-12 px-4 sm:px-6">
                <div className="max-w-7xl mx-auto space-y-6">
                    <div>
                        <p className="label-tracking text-gold">Admin Console</p>
                        <h1 className="font-serif text-4xl sm:text-5xl mt-2">Mission Control</h1>
                    </div>

                    {/* STATS */}
                    {stats && (
                        <section className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="admin-stats">
                            <StatTile label="Total Users" value={stats.total_users} />
                            <StatTile label="With Charts" value={stats.users_with_birth_info} />
                            <StatTile label="Verified" value={stats.verified_users} accent />
                            <StatTile label="Premium" value={stats.premium_users} accent />
                            <StatTile label="Matches" value={stats.total_matches} />
                            <StatTile label="Messages" value={stats.total_messages} />
                        </section>
                    )}

                    {/* RECENT SIGNUPS QUEUE */}
                    <section className="glass-gold p-5" data-testid="admin-recent-queue">
                        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
                            <div>
                                <p className="label-tracking text-gold flex items-center gap-2">
                                    <Clock size={12} weight="duotone" /> Moderation Queue
                                </p>
                                <h2 className="font-serif text-2xl mt-1">Recent signups ({recent.length})</h2>
                            </div>
                            <select
                                value={recentHours}
                                onChange={(e) => { const h = parseInt(e.target.value); setRecentHours(h); loadRecent(h); }}
                                className="px-3 py-1.5 rounded-lg bg-[#0A0A1A] border border-white/10 outline-none text-xs"
                                data-testid="recent-hours-select"
                            >
                                <option value="1">Last hour</option>
                                <option value="6">Last 6 hours</option>
                                <option value="24">Last 24 hours</option>
                                <option value="168">Last 7 days</option>
                            </select>
                        </div>
                        {recent.length === 0 ? (
                            <p className="text-muted text-sm italic">No new signups in this window.</p>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {recent.map((u) => {
                                    const conf = u.verification_confidence;
                                    const status = u.verified
                                        ? { label: u.verified_by_admin ? "Admin ✓" : `AI ✓ (${conf}%)`, color: "text-emerald-300" }
                                        : conf != null
                                            ? { label: `Failed (${conf}%)`, color: "text-red-300" }
                                            : { label: "No selfie", color: "text-muted" };
                                    return (
                                        <div key={u.user_id} className="p-3 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3" data-testid={`queue-${u.user_id}`}>
                                            <div className="w-11 h-11 rounded-full bg-white/5 border border-white/10 overflow-hidden flex items-center justify-center flex-shrink-0">
                                                {u.photos?.[0] ? (
                                                    <img src={fileUrl(u.photos[0].storage_path)} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    <span className="text-gold font-serif text-xl">{SIGN_GLYPH[u.sun] || "?"}</span>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-serif text-sm truncate">{u.name}{u.age ? `, ${u.age}` : ""}</p>
                                                <p className="text-[10px] text-muted truncate">{u.email}</p>
                                                <p className={`text-[10px] mt-0.5 ${status.color}`}>{status.label}</p>
                                            </div>
                                            <div className="flex flex-col gap-1">
                                                <ActionBtn
                                                    title={u.verified ? "Un-verify" : "Verify manually"}
                                                    active={u.verified}
                                                    onClick={() => setFlag(u.user_id, "verified", !u.verified, "Verified")}
                                                    testId={`queue-verify-${u.user_id}`}
                                                >
                                                    <ShieldCheck size={12} />
                                                </ActionBtn>
                                                <ActionBtn
                                                    title="Hide user"
                                                    active={u.hidden}
                                                    onClick={() => setFlag(u.user_id, "hidden", !u.hidden, "Hidden")}
                                                    testId={`queue-hide-${u.user_id}`}
                                                >
                                                    <EyeSlash size={12} />
                                                </ActionBtn>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    {/* FILTERS */}
                    <section className="glass p-4 flex flex-wrap items-center gap-3" data-testid="admin-filters">
                        <div className="relative flex-1 min-w-[200px]">
                            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                            <input
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search email or name..."
                                className="w-full pl-9 pr-3 py-2 rounded-lg bg-white/5 border border-white/10 outline-none focus:border-gold text-sm"
                                data-testid="admin-search"
                            />
                        </div>
                        <FilterSelect label="Premium" value={filter.premium} onChange={(v) => setFilter({ ...filter, premium: v })} testId="filter-premium" />
                        <FilterSelect label="Verified" value={filter.verified} onChange={(v) => setFilter({ ...filter, verified: v })} testId="filter-verified" />
                        <FilterSelect label="Admin" value={filter.admin} onChange={(v) => setFilter({ ...filter, admin: v })} testId="filter-admin" />
                        <p className="text-xs text-muted ml-auto">{total} users</p>
                    </section>

                    {/* TABLE */}
                    <section className="glass overflow-hidden" data-testid="admin-user-table">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="border-b border-white/10">
                                    <tr className="text-left">
                                        <Th>User</Th>
                                        <Th>Sun / Moon / Rising</Th>
                                        <Th>Joined</Th>
                                        <Th>Status</Th>
                                        <Th>Actions</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading && (
                                        <tr><td colSpan={5} className="px-4 py-8 text-center text-muted">Loading...</td></tr>
                                    )}
                                    {!loading && users.length === 0 && (
                                        <tr><td colSpan={5} className="px-4 py-8 text-center text-muted">No users found</td></tr>
                                    )}
                                    {users.map((u) => (
                                        <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${u.user_id}`}>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                                                        {u.photos?.[0] ? (
                                                            <img src={fileUrl(u.photos[0].storage_path)} alt="" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <span className="text-gold font-serif">{SIGN_GLYPH[u.sun] || "?"}</span>
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <p className="font-serif text-base truncate">{u.name}{u.age ? `, ${u.age}` : ""}</p>
                                                        <p className="text-xs text-muted truncate">{u.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-xs text-muted">
                                                {u.sun ? `${SIGN_GLYPH[u.sun]} ${u.sun} · ☽ ${u.moon} · ↑ ${u.rising}` : <span className="italic">No chart</span>}
                                            </td>
                                            <td className="px-4 py-3 text-xs text-muted whitespace-nowrap">
                                                {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap gap-1">
                                                    {u.is_admin && <Badge color="gold">Admin</Badge>}
                                                    {u.subscription_active && <Badge color="gold">Premium</Badge>}
                                                    {u.verified && <Badge color="emerald">Verified</Badge>}
                                                    {u.hidden && <Badge color="red">Hidden</Badge>}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <ActionBtn
                                                        title={u.is_admin ? "Remove admin" : "Make admin"}
                                                        active={u.is_admin}
                                                        onClick={() => setFlag(u.user_id, "admin", !u.is_admin, "Admin")}
                                                        testId={`toggle-admin-${u.user_id}`}
                                                    >
                                                        <Crown size={14} />
                                                    </ActionBtn>
                                                    <ActionBtn
                                                        title={u.subscription_active ? "Remove Premium" : "Grant Premium (1 year)"}
                                                        active={u.subscription_active}
                                                        onClick={() => setFlag(u.user_id, "premium", !u.subscription_active, "Premium")}
                                                        testId={`toggle-premium-${u.user_id}`}
                                                    >
                                                        <Star size={14} />
                                                    </ActionBtn>
                                                    <ActionBtn
                                                        title={u.verified ? "Un-verify" : "Verify manually"}
                                                        active={u.verified}
                                                        onClick={() => setFlag(u.user_id, "verified", !u.verified, "Verified")}
                                                        testId={`toggle-verified-${u.user_id}`}
                                                    >
                                                        <ShieldCheck size={14} />
                                                    </ActionBtn>
                                                    <ActionBtn
                                                        title={u.hidden ? "Un-hide" : "Hide / ban"}
                                                        active={u.hidden}
                                                        onClick={() => setFlag(u.user_id, "hidden", !u.hidden, "Hidden")}
                                                        testId={`toggle-hidden-${u.user_id}`}
                                                    >
                                                        {u.hidden ? <Eye size={14} /> : <EyeSlash size={14} />}
                                                    </ActionBtn>
                                                    <button
                                                        onClick={() => deleteUser(u.user_id, u.name)}
                                                        className="w-7 h-7 rounded-full border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center justify-center transition-colors"
                                                        title="Delete user"
                                                        data-testid={`delete-${u.user_id}`}
                                                    >
                                                        <Trash size={14} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            </main>
        </>
    );
}

function StatTile({ label, value, accent }) {
    return (
        <div className={`${accent ? "glass-gold" : "glass"} p-4 text-center`}>
            <p className="font-serif text-3xl text-gold">{value}</p>
            <p className="label-tracking text-[10px] text-muted mt-1">{label}</p>
        </div>
    );
}

function Th({ children }) {
    return <th className="px-4 py-3 label-tracking text-[10px] text-muted font-normal">{children}</th>;
}

function Badge({ children, color }) {
    const colors = {
        gold: "bg-gold/15 text-gold border-gold/30",
        emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
        red: "bg-red-500/10 text-red-300 border-red-500/30",
    };
    return (
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${colors[color] || colors.gold}`}>
            {children}
        </span>
    );
}

function FilterSelect({ label, value, onChange, testId }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="px-3 py-2 rounded-lg bg-[#0A0A1A] border border-white/10 outline-none focus:border-gold text-sm"
            data-testid={testId}
        >
            <option value="">All {label}</option>
            <option value="true">{label} ✓</option>
            <option value="false">Not {label}</option>
        </select>
    );
}

function ActionBtn({ children, title, active, onClick, testId }) {
    return (
        <button
            onClick={onClick}
            title={title}
            data-testid={testId}
            className={`w-7 h-7 rounded-full border flex items-center justify-center transition-colors ${
                active
                    ? "border-gold/50 bg-gold/15 text-gold"
                    : "border-white/15 text-muted hover:border-gold/40 hover:text-gold"
            }`}
        >
            {children}
        </button>
    );
}
